import { Box, Grid } from '@mui/material';
import _ from 'lodash';
import Link from 'next/link';
import PropTypes, { object } from 'prop-types';
import React from 'react';

import { RATERS, SPACE, URLS } from '../../constants';
import { ANCAP_TEST_CATEGORY_IDENTIFIERS } from '../../constants/statuses';
import { getSafetyFeatureStatus } from '../../utils/safety-features';
import { getAncapCategoryChildTests } from '../../utils/ancap-record';
import { useGutterSpacing } from '../../hooks/use-gutter-spacing';
import FeatureTile from '../feature-tile';
import { Small } from '../type';

import {
  AncapIcon,
  Bold,
  AncapTestCategoryHeading,
  ChildCarSeatsImg,
  ChildCarSeatsInnerWrapper,
  ChildCarSeatsLink,
  ChildCarSeatsWrapper,
  Divider,
  FeatureTileGrid,
  FeatureTileGridWrapper,
  AncapTestCategory,
  SafetyFeatureRow,
  StyledHeading,
  StyledSubHeading,
} from './styles';

// @ts-expect-error [🤖 PLEASE FIX 🤖]: Parameter 'ancapTestCategory' implicitly has an 'any' type.
const getIconForCategory = (ancapTestCategory) => {
  switch (ancapTestCategory) {
    case ANCAP_TEST_CATEGORY_IDENTIFIERS.ACTIVE:
      return <AncapIcon src="/images/ancap-safety-assist-icon.jpg" alt="ANCAP Icon: Active Safety Assist" />;
    case ANCAP_TEST_CATEGORY_IDENTIFIERS.ADULTS:
      return <AncapIcon src="/images/ancap-adult-protection-icon.jpg" alt="ANCAP Icon: Safety Protection for Adults" />;
    case ANCAP_TEST_CATEGORY_IDENTIFIERS.KIDS:
      return (
        <AncapIcon src="/images/ancap-child-protection-icon.jpg" alt="ANCAP Icon: Safety Protection for Children" />
      );
    case ANCAP_TEST_CATEGORY_IDENTIFIERS.PEDESTRIANS:
      return (
        <AncapIcon
          src="/images/ancap-pedestrian-protection-icon.jpg"
          alt="ANCAP Icon: Safety Protection for Pedestrians"
        />
      );
    case ANCAP_TEST_CATEGORY_IDENTIFIERS.VRU:
      return (
        <AncapIcon
          src="/images/ancap-pedestrian-protection-icon.jpg"
          alt="ANCAP Icon: Safety Protection for Vulnerable Road Users"
        />
      );
    default:
      break;
  }
  return null;
};

// @ts-expect-error [🤖 PLEASE FIX 🤖]: Binding element 'test' implicitly has an 'any' type.
const AncapTestResult = ({ test, testValue, maxValue, units }) => {
  const isCategoryOverallScore = units === 'percentage';
  const Element = isCategoryOverallScore ? Bold : 'span';

  const maxValueString = maxValue ? `out of ${maxValue}` : '';

  return (
    <SafetyFeatureRow>
      <dt>
        <Element>{test.title}</Element>
      </dt>
      <dd>{isCategoryOverallScore ? <Element>{testValue}%</Element> : `${testValue} ${maxValueString}`}</dd>
    </SafetyFeatureRow>
  );
};

const childCarSeatsBlock = (
  <ChildCarSeatsWrapper>
    <ChildCarSeatsImg src="/images/child-car-seats.png" />
    <ChildCarSeatsInnerWrapper>
      <h5>Looking for more information about car seats?</h5>
      <Box my={SPACE.X0}>
        <Small>
          The correct child car seats for children&apos;s age and size are an important aspect of keeping them safe when
          in the car.
        </Small>
      </Box>
      <Link href={URLS.CHILD_CAR_SEATS}>
        <ChildCarSeatsLink>Go to www.childcarseats.com.au</ChildCarSeatsLink>
      </Link>
    </ChildCarSeatsInnerWrapper>
  </ChildCarSeatsWrapper>
);

const FeatureTestGrouping = ({
  // @ts-expect-error [🤖 PLEASE FIX 🤖]: Binding element 'heading' implicitly has an 'any' type.
  heading,
  ancapTestCategoryResult = null,
  // @ts-expect-error [🤖 PLEASE FIX 🤖]: Binding element 'subHeading' implicitly has an 'any' type.
  subHeading,
  safetyFeatures = [],
  ratingProvider = null,
  relatedAncapCategory = null,
}) => {
  return (
    <Grid container spacing={useGutterSpacing()} justifyContent="center">
      <Grid item {...{ xs: 12 }}>
        <StyledHeading as="h2">{heading}</StyledHeading>
        <StyledSubHeading>{subHeading}</StyledSubHeading>
        <Grid container spacing={useGutterSpacing()} justifyContent="center">
          <Grid item {...{ lg: 4, md: 5, xs: 12 }}>
            {ratingProvider === RATERS.ANCAP && !_.isNil(relatedAncapCategory) && (
              <>
                {/* @ts-expect-error [🤖 PLEASE FIX 🤖]: Property 'title' does not exist on type 'never'. */}
                {!_.isEmpty(relatedAncapCategory.title) && (
                  <AncapTestCategory>
                    <AncapTestCategoryHeading>
                      {/* @ts-expect-error [🤖 PLEASE FIX 🤖]: Property 'slug' does not exist on type 'never'. */}
                      {getIconForCategory(relatedAncapCategory.slug)}
                      {/* @ts-expect-error [🤖 PLEASE FIX 🤖]: Property 'title' does not exist on type 'never'. */}
                      {relatedAncapCategory.title}
                    </AncapTestCategoryHeading>
                    {_.includes(ancapTestCategoryResult, '%') && ancapTestCategoryResult}
                  </AncapTestCategory>
                )}
                {/* @ts-expect-error [🤖 PLEASE FIX 🤖]: Property 'tests' does not exist on type 'never'. */}
                {_.isEmpty(relatedAncapCategory.tests) ? (
                  // @ts-expect-error [🤖 PLEASE FIX 🤖]: Property 'categoryDescription' does not exist on type 'never'.
                  <Small>{relatedAncapCategory.categoryDescription}</Small>
                ) : (
                  _.map(getAncapCategoryChildTests(relatedAncapCategory), ({ test, testValue, maxValue, units }) => {
                    return (
                      <AncapTestResult {...{ test, testValue, maxValue, units }} key={`${test.id}-${test.title}`} />
                    );
                  })
                )}
              </>
            )}
          </Grid>
          <Grid item {...{ lg: 8, md: 7, xs: 12 }}>
            <FeatureTileGridWrapper>
              <FeatureTileGrid container spacing={useGutterSpacing()}>
                {_.map(safetyFeatures, (safetyFeature) => (
                  // @ts-expect-error [🤖 PLEASE FIX 🤖]: Property 'id' does not exist on type 'never'.
                  <Grid key={safetyFeature.id} item {...{ lg: 6, md: 10, xs: 12 }}>
                    <FeatureTile
                      // @ts-expect-error [🤖 PLEASE FIX 🤖]: Property 'id' does not exist on type 'never'.
                      id={safetyFeature.id}
                      // @ts-expect-error [🤖 PLEASE FIX 🤖]: Property 'featureIcon' does not exist on type 'never'.
                      iconUrl={_.first(safetyFeature.featureIcon) ? _.first(safetyFeature.featureIcon).url : null}
                      // @ts-expect-error [🤖 PLEASE FIX 🤖]: Property 'title' does not exist on type 'never'.
                      heading={safetyFeature.title}
                      // @ts-expect-error [🤖 PLEASE FIX 🤖]: Property 'featureDescription' does not exist on type 'never'.
                      body={safetyFeature.featureDescription}
                      // @ts-expect-error [🤖 PLEASE FIX 🤖]: Type 'string | null' is not assignable to type 'string | undefined'.
                      detailUrl={safetyFeature.isPrimaryFeature ? `/${safetyFeature.uri}` : null}
                      statusKey={getSafetyFeatureStatus(safetyFeature)}
                    />
                  </Grid>
                ))}
                {heading === 'Crash protection for kids' ? (
                  <Grid item {...{ lg: 12, md: 10, xs: 12 }}>
                    {childCarSeatsBlock}
                  </Grid>
                ) : null}
              </FeatureTileGrid>
            </FeatureTileGridWrapper>
          </Grid>
        </Grid>
        <Divider />
      </Grid>
    </Grid>
  );
};

FeatureTestGrouping.propTypes = {
  heading: PropTypes.string.isRequired,
  subHeading: PropTypes.string.isRequired,
  ancapTestCategoryResult: PropTypes.string,
  safetyFeatures: PropTypes.arrayOf(
    PropTypes.shape({
      iconUrl: PropTypes.string,
      heading: PropTypes.string,
      body: PropTypes.string,
      detailUrl: PropTypes.string,
      statusKey: PropTypes.string,
    })
  ),
  relatedAncapCategory: PropTypes.shape({
    id: PropTypes.string,
    title: PropTypes.string,
    slug: PropTypes.string,
    categoryDescription: PropTypes.string,
    tests: PropTypes.arrayOf(object),
  }),
  ratingProvider: PropTypes.string,
};

export default FeatureTestGrouping;
